import { AUDIT_BASE_URL } from "../../utils/ApiUrls";

const PUBLISHED_AUDIT_PLAN_BASE_URL = `${AUDIT_BASE_URL}/published-plans`;

class PublishedAuditPlanService {
  /**
   * Get published audit plans where the user is a party of the audit program (authenticated)
   * @param {string} accessToken - JWT access token
   * @param {Object} filters - Filter parameters
   * @param {Object} pagination - Pagination parameters
   */
  async getPublishedAuditPlans(accessToken, filters = {}, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.search) params.append('search', filters.search);
      if (filters.program) params.append('program', filters.program);
      if (filters.ordering) params.append('ordering', filters.ordering);
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plans',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plans:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plans',
      };
    }
  }

  /**
   * Get detailed view of a published audit plan (authenticated)
   * Only accessible if user is a party of the audit program
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   */
  async getPublishedAuditPlanDetail(accessToken, planId) {
    try {
      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/${planId}/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plan details',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plan details:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plan details',
      };
    }
  }


  
  /**
   * Download an audit plan attachment
   * @param {string} accessToken - JWT access token
   * @param {number} attachmentId - The attachment ID to download
   */
  async downloadAttachment(accessToken, attachmentId) {
    try {
      const response = await fetch(`${AUDIT_BASE_URL}/plan-attachments/${attachmentId}/download/`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        return {
          success: false,
          error: errorData.detail || 'Failed to download attachment'
        };
      }

      // Get the filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `attachment_${attachmentId}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Get the blob
      const blob = await response.blob();
      
      // Create a download link and trigger it
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (error) {
      console.error('Error downloading attachment:', error);
      return {
        success: false,
        error: error.message || 'An error occurred while downloading the attachment'
      };
    }
  }


  /**
   * Get sections for a published audit plan (authenticated)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   * @param {Object} pagination - Pagination parameters
   */
  async getPublishedAuditPlanSections(accessToken, planId, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/${planId}/sections/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plan sections',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plan sections:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plan sections',
      };
    }
  }

  /**
   * Get questions for a section in a published audit plan (authenticated)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   * @param {number} sectionId - Section ID
   * @param {Object} pagination - Pagination parameters
   */
  async getPublishedAuditPlanSectionQuestions(accessToken, planId, sectionId, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/${planId}/sections/${sectionId}/questions/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plan questions',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plan questions:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plan questions',
      };
    }
  }

  /**
   * Get recommendations for a question in a published audit plan (authenticated)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   * @param {number} sectionId - Section ID
   * @param {number} questionId - Question ID
   * @param {Object} pagination - Pagination parameters
   */
  async getPublishedAuditPlanSectionQuestionsRecommendationActions(accessToken, planId, sectionId, questionId, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/${planId}/sections/${sectionId}/questions/${questionId}/recommendations/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plan recommendations',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plan recommendations:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plan recommendations',
      };
    }
  }

  /**
   * Get follow-ups for a recommendation in a published audit plan (authenticated)
   * @param {string} accessToken - JWT access token
   * @param {number} planId - Audit plan ID
   * @param {number} recommendationId - Recommendation ID
   * @param {Object} pagination - Pagination parameters
   */
  async getPublishedAuditPlanSectionQuestionsRecommendationFollowups(accessToken, planId, recommendationId, pagination = {}) {
    try {
      const params = new URLSearchParams();
      
      // Add pagination
      if (pagination.page) params.append('page', pagination.page);
      if (pagination.page_size) params.append('page_size', pagination.page_size);

      const response = await fetch(`${PUBLISHED_AUDIT_PLAN_BASE_URL}/${planId}/recommendations/${recommendationId}/followups/?${params}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          error: data.detail || 'Failed to fetch published audit plan follow-ups',
          errorData: data,
        };
      }

      return {
        success: true,
        data: data,
      };
    } catch (error) {
      console.error('Error fetching published audit plan follow-ups:', error);
      return {
        success: false,
        error: 'Network error occurred while fetching published audit plan follow-ups',
      };
    }
  }
}

const publishedAuditPlanService = new PublishedAuditPlanService();
export default publishedAuditPlanService;

